import { useSyncExternalStore } from 'react'

/**
 * The interface language: one owner, this module. Everything else reads it through `useLang` /
 * `useT`, or asks `setLang` to change it.
 *
 * Messages live next to the feature that shows them (`defineMessages` in each feature), not in
 * one big file, so a feature is removable with its words, and the type makes a missing
 * translation a compile error rather than an English word in a Romanian screen.
 */

export const LANGS = ['en', 'ro', 'ru', 'fr', 'it', 'kk', 'es', 'pt', 'de'] as const
export type Lang = (typeof LANGS)[number]

export const LANG_LABEL: Record<Lang, string> = {
	en: 'English',
	ro: 'Română',
	ru: 'Русский',
	fr: 'Français',
	it: 'Italiano',
	kk: 'Қазақша',
	es: 'Español',
	pt: 'Português',
	de: 'Deutsch',
}

const STORAGE_KEY = 'tt-lang'

/**
 * Her own choice first, then the device's languages in order, then English. A landing page in
 * her language links here with `?lang=`, which counts as her choice.
 */
function detect(): Lang {
	try {
		const asked = new URLSearchParams(location.search).get('lang')
		if (asked && (LANGS as readonly string[]).includes(asked)) {
			localStorage.setItem(STORAGE_KEY, asked)
			return asked as Lang
		}
		const saved = localStorage.getItem(STORAGE_KEY)
		if (saved && (LANGS as readonly string[]).includes(saved)) return saved as Lang
	} catch {
		// Storage blocked: fall through to the device.
	}
	const device =
		typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language])
	for (const tag of device) {
		const base = tag?.toLowerCase().split('-')[0]
		// Moldovan is Romanian; the tag still turns up on older devices.
		if (base === 'ro' || base === 'mo') return 'ro'
		if (base === 'ru') return 'ru'
		if (base === 'fr') return 'fr'
		if (base === 'it') return 'it'
		// Kazakhstan's Russian (`ru-KZ`) stays Russian: only a Kazakh device gets Kazakh.
		if (base === 'kk') return 'kk'
		if (base === 'es') return 'es'
		if (base === 'pt') return 'pt'
		if (base === 'de') return 'de'
		if (base === 'en') return 'en'
	}
	return 'en'
}

let current: Lang = detect()
const listeners = new Set<() => void>()
if (typeof document !== 'undefined') document.documentElement.lang = current

export function getLang(): Lang {
	return current
}

export function setLang(next: Lang): void {
	if (next === current) return
	current = next
	try {
		localStorage.setItem(STORAGE_KEY, next)
	} catch {
		// Not remembered across visits, still applied now.
	}
	document.documentElement.lang = next
	for (const l of listeners) l()
}

const subscribe = (l: () => void) => {
	listeners.add(l)
	return () => {
		listeners.delete(l)
	}
}

export const useLang = (): Lang => useSyncExternalStore(subscribe, getLang, getLang)

/** A message is a string, or a function of its values: that covers interpolation and plurals. */
type Message = string | ((...args: any[]) => string)
type Messages = Record<string, Message>

/** Every language must carry every English key, with the same shape. */
export function defineMessages<M extends Messages>(all: {
	en: M
	ro: { [K in keyof M]: M[K] }
	ru: { [K in keyof M]: M[K] }
	fr: { [K in keyof M]: M[K] }
	it: { [K in keyof M]: M[K] }
	kk: { [K in keyof M]: M[K] }
	es: { [K in keyof M]: M[K] }
	pt: { [K in keyof M]: M[K] }
	de: { [K in keyof M]: M[K] }
}) {
	return all
}

/** The messages for the current language. Re-renders the component when the language changes. */
export function useT<M extends Messages>(all: {
	en: M
	ro: M
	ru: M
	fr: M
	it: M
	kk: M
	es: M
	pt: M
	de: M
}): M {
	return all[useLang()]
}

/** Outside React (query hooks, stores): the current language's messages, read once. */
export function messagesNow<M extends Messages>(all: {
	en: M
	ro: M
	ru: M
	fr: M
	it: M
	kk: M
	es: M
	pt: M
	de: M
}): M {
	return all[current]
}

/**
 * The right form for a count. Russian has three ("1 упражнение", "2 упражнения", "5 упражнений"),
 * Romanian has "1 exercițiu", "2 exerciții", "20 de exerciții"; Intl knows the rules.
 */
export function plural(
	n: number,
	forms: { one: string; few?: string; many?: string; other: string },
): string {
	const rule = new Intl.PluralRules(current).select(n) as keyof typeof forms
	return (forms[rule] ?? forms.other).replace('#', String(n))
}
